import React, { useState, useEffect } from 'react';
import { sopsAPI } from '../services/api';
import { 
  FileArchive, 
  Download, 
  Calendar, 
  FileText,
  Loader2,
  Info,
  Truck,
  AlertTriangle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ReportsPage = () => {
  const [sops, setSops] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fleetLoading, setFleetLoading] = useState(false);
  const [sopFilter, setSopFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    const fetchSops = async () => {
      try {
        const response = await sopsAPI.list();
        setSops(response.data || []);
      } catch (error) {
        console.error('Failed to fetch SOPs:', error);
      }
    };
    fetchSops();
  }, []);

  const handleDownloadEvidencePack = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (sopFilter && sopFilter !== 'all') {
        params.append('sop_code', sopFilter);
      }
      if (dateFrom) {
        params.append('date_from', dateFrom);
      }
      if (dateTo) {
        params.append('date_to', dateTo);
      }

      const token = localStorage.getItem('rtms_token');
      const response = await fetch(`${API_URL}/export/evidence-pack?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to generate evidence pack');
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'RTMS_EvidencePack.zip';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) {
          filename = match[1];
        }
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success('Evidence Pack downloaded successfully');
    } catch (error) {
      console.error('Failed to download evidence pack:', error);
      toast.error(error.message || 'Failed to download evidence pack');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadFleetExpiryReport = async () => {
    setFleetLoading(true);
    try {
      const token = localStorage.getItem('rtms_token');
      const response = await fetch(`${API_URL}/reports/fleet-expiry`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to generate fleet expiry report');
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'RTMS_Fleet_Expiry_Report.xlsx';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) {
          filename = match[1];
        }
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success('Fleet Expiry Report downloaded successfully');
    } catch (error) {
      console.error('Failed to download fleet expiry report:', error);
      toast.error(error.message || 'Failed to download report');
    } finally {
      setFleetLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reports-page">
      {/* Header */}
      <div>
        <h1 
          className="text-3xl font-bold text-slate-900"
          style={{ fontFamily: 'Barlow Condensed' }}
        >
          REPORTS & EXPORTS
        </h1>
        <p className="text-slate-500 mt-1">
          Generate compliance reports and audit evidence packs
        </p>
      </div>

      {/* Fleet Expiry Report - NEW */}
      <Card className="max-w-2xl">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-sm flex items-center justify-center">
              <Truck className="w-5 h-5 text-amber-700" />
            </div>
            <div>
              <CardTitle style={{ fontFamily: 'Barlow Condensed' }}>FLEET EXPIRY REPORT</CardTitle>
              <CardDescription>
                Vehicle and trailer license & port permit expiry status
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-medium mb-1">Report Contents:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>All vehicle and trailer details (Reg, VIN, Make, Model, etc.)</li>
                  <li>Weight specifications (GVM, Tare, Payload, GCM, GCP, Axle Payloads)</li>
                  <li>License Expiry Date & Days Remaining</li>
                  <li>Port Permit Expiry Date & Days Remaining</li>
                  <li>Color-coded status (Expired, Warning, OK)</li>
                </ul>
              </div>
            </div>
          </div>

          <Button 
            onClick={handleDownloadFleetExpiryReport}
            disabled={fleetLoading}
            className="w-full gap-2"
            data-testid="download-fleet-expiry-btn"
          >
            {fleetLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Generating Report...
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                Download Fleet Expiry Report
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Evidence Pack Export */}
      <Card className="max-w-2xl">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-slate-900 rounded-sm flex items-center justify-center">
              <FileArchive className="w-5 h-5 text-white" />
            </div>
            <div>
              <CardTitle style={{ fontFamily: 'Barlow Condensed' }}>EVIDENCE PACK EXPORT</CardTitle>
              <CardDescription>
                Generate a ZIP archive for audit purposes
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-slate-50 border border-slate-200 rounded-md p-4">
            <div className="flex items-start gap-2">
              <Info className="w-5 h-5 text-slate-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-slate-600">
                <p className="font-medium mb-1">Evidence Pack Contents:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Manifest.xlsx - Summary of all included records</li>
                  <li>Submissions folder - Excel and PDF exports organized by status</li>
                  <li>Corrective Actions summary</li>
                  <li>Attachment references</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="sop-filter">SOP Filter</Label>
              <Select value={sopFilter} onValueChange={setSopFilter}>
                <SelectTrigger data-testid="sop-filter-select">
                  <SelectValue placeholder="All SOPs" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All SOPs</SelectItem>
                  {sops.map(sop => (
                    <SelectItem key={sop.code} value={sop.code}>
                      {sop.code} - {sop.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="date-from">Date From</Label>
              <Input
                id="date-from"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                data-testid="date-from-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="date-to">Date To</Label>
              <Input
                id="date-to"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                data-testid="date-to-input"
              />
            </div>
          </div>

          <Button 
            onClick={handleDownloadEvidencePack}
            disabled={loading}
            className="w-full gap-2"
            data-testid="download-evidence-pack-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Generating Evidence Pack...
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                Download Evidence Pack
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Quick Export Links */}
      <Card className="max-w-2xl">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-slate-100 rounded-sm flex items-center justify-center">
              <FileText className="w-5 h-5 text-slate-700" />
            </div>
            <div>
              <CardTitle style={{ fontFamily: 'Barlow Condensed' }}>QUICK EXPORTS</CardTitle>
              <CardDescription>
                Common report formats and templates
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <Button variant="outline" className="justify-start gap-2" disabled>
              <FileText className="w-4 h-4" />
              Compliance Summary
              <span className="ml-auto text-xs text-slate-400">Soon</span>
            </Button>
            <Button variant="outline" className="justify-start gap-2" disabled>
              <Calendar className="w-4 h-4" />
              Driver Expiry Report
              <span className="ml-auto text-xs text-slate-400">Soon</span>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ReportsPage;
